import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, BookOpenText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";

const STORAGE_KEY = "ncl_daily_verse_shown_date";

/**
 * URL params that indicate the user just tapped a push notification and
 * wants to see that specific piece of content. If any of these are present
 * when the app loads (or become present later), the DailyVerseModal will
 * refuse to open (or immediately close) so it doesn't sit on top of the
 * notification detail modal and appear to "freeze" the app.
 *
 * The verse will be shown on the NEXT app open (it's marked as seen only
 * on Amen / Close, so skipping today doesn't wipe it out).
 *
 * ``nt`` (notification-tap) is the universal marker the service worker
 * appends to EVERY notification target URL, including plain paths like
 * ``/events`` and ``/prayers`` that don't already have a query string.
 * Without it we could only suppress on ``?news=`` / ``?broadcast=`` deep
 * links; event / prayer / generic pushes would let the verse pop up on
 * top of the destination page and trap the user's tap.
 */
const NOTIFICATION_PARAMS = ["broadcast", "news", "nt"];

function hasNotificationParam(search) {
  const params = new URLSearchParams(search || "");
  return NOTIFICATION_PARAMS.some((k) => params.get(k));
}

/**
 * Safety net: detect whether ANOTHER modal is already blocking the page.
 *
 * Radix UI's Dialog primitive sets ``data-scroll-locked`` on <body> while
 * a dialog is open (used for the news detail modal, broadcast modal,
 * event detail modal, registration modal, etc.). We also fall back to
 * checking ``body.style.overflow`` — some libraries set that directly.
 *
 * If either is present when we're about to open the verse, we bail. This
 * catches the edge case where a deep-link modal is already opening and
 * we don't want to slam the verse on top of it.
 */
function anotherModalIsOpen() {
  if (typeof document === "undefined" || !document.body) return false;
  try {
    if (document.body.hasAttribute("data-scroll-locked")) return true;
    if ((document.body.style.overflow || "").toLowerCase() === "hidden") return true;
  } catch (_e) {}
  return false;
}

/**
 * Daily verse popup shown ONCE per UTC date.
 *
 * Uses React Portal to render directly into `document.body` so the modal
 * escapes any ancestor stacking context or CSS rules (e.g. `.noise > *` in
 * index.css used to force `position: relative` on direct children, which was
 * pushing the modal inline at the bottom of the page instead of as an
 * overlay).
 *
 * Body scroll is locked while the modal is open so users can't scroll past it.
 *
 * Coexistence with notification modals
 * ------------------------------------
 * If the URL carries a ``?broadcast=<id>`` or ``?news=<id>`` param, the user
 * just tapped a push notification. In that case we DO NOT show the verse
 * modal — otherwise its portal (zIndex 9999) sits on top of the notification
 * detail modal, blocking every interaction and giving the impression the app
 * has frozen. The verse dedupe stamp is only written on Amen/Close, so
 * skipping today just means the member will see it on their next app open.
 */
export function DailyVerseModal() {
  const { t, lang } = useI18n();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [bgLoaded, setBgLoaded] = useState(false);

  // Latch a "block for this app session" flag if the app was opened with a
  // notification param (or if the URL later grows one before we've opened).
  // This is a REF, not state, so it stays sticky across re-renders even if
  // HomePage strips the ?news= param out of the URL after fetching the
  // notification detail. Without this, the following race happens:
  //   1. mount: URL has ?news=<id> — we correctly skip fetching the verse
  //   2. HomePage's own useEffect fires, fetches the news item, and calls
  //      navigate({search: ""}, replace) to clean the URL
  //   3. location.search changes -> our useEffect re-runs -> URL is now
  //      clean -> we cheerfully open the verse ON TOP of the news modal
  // The latch pins the "skip" decision so step 3 does nothing.
  const blockedRef = useRef(hasNotificationParam(
    typeof window !== "undefined" ? window.location.search : ""
  ));

  // Fetch today's verse and decide whether to show. Runs once on mount and
  // again if the URL changes (so a client-side nav that adds ?broadcast=X
  // still closes us — mirrors what a hard-tap on the notification does).
  useEffect(() => {
    // Latch: if we ever detect a notification param, block for the rest of
    // this session. Once latched we never un-latch — the user should see
    // their notification, not the verse. The verse re-appears on the next
    // fresh app open when there's no notification pending.
    if (hasNotificationParam(location.search)) {
      blockedRef.current = true;
    }
    if (blockedRef.current) {
      if (open) setOpen(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const shown = (typeof localStorage !== "undefined")
          ? localStorage.getItem(STORAGE_KEY)
          : null;
        const { data: today } = await api.get("/verse/today");
        if (cancelled) return;
        if (shown === today.date) return; // already shown today
        // Re-check the URL and latch right before opening — the notification
        // param may have been added while the /verse/today request was in
        // flight (async race).
        if (hasNotificationParam(window.location.search) || blockedRef.current) {
          blockedRef.current = true;
          return;
        }
        // Safety net: never open the verse on top of another modal.
        // A Radix Dialog (news detail, broadcast, event detail, registration
        // modal, etc.) that opened between mount and this callback would
        // otherwise be covered by our fullscreen Portal, and the user's
        // tap on the underlying modal would be swallowed -- exactly the
        // "app is frozen" symptom reported for event notifications.
        if (anotherModalIsOpen()) {
          blockedRef.current = true;
          return;
        }
        setData(today);
        setOpen(true);
      } catch (_e) {
        // network error — skip silently
      }
    })();
    return () => { cancelled = true; };
  }, [location.search]);

  // Lock body scroll while the modal is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const close = () => {
    try {
      if (data?.date && typeof localStorage !== "undefined") {
        localStorage.setItem(STORAGE_KEY, data.date);
      }
    } catch (_e) { /* private mode / quota */ }
    // Defensive: force-release the body scroll lock BEFORE the state
    // change fires the exit animation. If for some reason our own
    // effect-cleanup gets skipped (e.g. Framer Motion exit animation
    // is interrupted, or another modal component overwrote the
    // captured ``prev`` value in the meantime), the page could stay
    // scroll-locked and appear frozen after close. Doing it here
    // guarantees the page is interactive the instant the user hits
    // "Amen" or the "X" button.
    try {
      if (typeof document !== "undefined" && document.body) {
        document.body.style.overflow = "";
      }
    } catch (_e) {}
    setOpen(false);
  };

  if (!data || typeof document === "undefined") return null;

  const ref = lang === "en" ? data.ref_en : data.ref_es;
  const text = lang === "en" ? data.text_en : data.text_es;

  // Inline-styled overlay so it can never be overridden by ancestor CSS.
  const overlayStyle = {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
    paddingTop: "max(1rem, env(safe-area-inset-top))",
    paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
  };

  const content = (
    <AnimatePresence>
      {open && (
        <motion.div
          style={overlayStyle}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          data-testid="daily-verse-modal"
          role="dialog"
          aria-modal="true"
          aria-label={t.home.dailyVerseTitle}
        >
          {/* Background image (fades in once loaded) */}
          <img
            src={data.background_url}
            alt=""
            onLoad={() => setBgLoaded(true)}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: bgLoaded ? 1 : 0,
              transition: "opacity 500ms ease",
            }}
          />
          {/* Dark gradient overlay for legibility */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.55) 50%, rgba(0,0,0,0.75) 100%)",
            }}
          />

          {/* Content */}
          <motion.div
            className="relative z-10 w-full max-w-md text-center text-white"
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 12, opacity: 0 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            style={{ position: "relative", zIndex: 1 }}
          >
            {/* Top-right close X */}
            <button
              type="button"
              onClick={close}
              aria-label={t.home.dailyVerseClose}
              className="absolute right-0 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white/90 hover:bg-white/25 active:scale-95 transition focus-visible:ring-2 focus-visible:ring-white/60"
              style={{ top: "-0.5rem", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
              data-testid="daily-verse-close-x"
            >
              <X className="h-5 w-5" />
            </button>

            <div
              className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.18em]"
              style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
            >
              <BookOpenText className="h-3.5 w-3.5" />
              {t.home.dailyVerseTitle}
            </div>

            <p
              className="font-serif-display mt-6 text-2xl leading-snug drop-shadow-md sm:text-3xl"
              data-testid="daily-verse-text"
            >
              &ldquo;{text}&rdquo;
            </p>

            <p
              className="font-serif-display mt-5 text-base font-medium tracking-wide drop-shadow"
              data-testid="daily-verse-ref"
            >
              {ref}
            </p>

            <div className="mt-8">
              <Button
                onClick={close}
                size="lg"
                className="rounded-full bg-white px-8 text-foreground hover:bg-white/90 shadow-lg"
                data-testid="daily-verse-amen"
              >
                {t.home.dailyVerseAmen}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Portal directly into document.body so it bypasses any ancestor stacking/positioning context
  return createPortal(content, document.body);
}
